import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MapPin, Plus, Edit, Trash2, RefreshCw } from 'lucide-react';

const AdminDestinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadDestinations();
  }, []);

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const loadDestinations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/destination-types', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDestinations(data.destination_types || []);
      } else {
        showMessage('Failed to load destinations', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteDestination = async (id) => {
    if (!window.confirm(`Delete destination type "${id}"?`)) return;

    try {
      const token = localStorage.getItem('jwt') || localStorage.getItem('adminToken');
      const response = await fetch(`/api/admin/destination-types/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        showMessage('Destination type deleted successfully', 'success');
        loadDestinations();
      } else {
        const data = await response.json();
        showMessage(data.detail || 'Failed to delete destination', 'error');
      }
    } catch (error) {
      showMessage(`Error: ${error.message}`, 'error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Destination Types</h1>
          <p className="text-muted-foreground">
            Manage destination categories and seasonal patterns
          </p>
        </div>
        
        <div className="flex space-x-2">
          <Button onClick={loadDestinations} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Destination
          </Button>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-md ${
          messageType === 'error' ? 'bg-destructive/10 text-destructive' :
          messageType === 'success' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
          'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
        }`}>
          {message}
        </div>
      )}

      {/* Destinations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MapPin className="h-5 w-5" />
            <span>Destination Types ({destinations.length})</span>
          </CardTitle>
          <CardDescription>
            Configure destination categories for demand forecasting
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Loading destinations...</p>
            </div>
          ) : destinations.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No destination types found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">ID</th>
                    <th className="text-left py-3 px-4 font-medium">Name</th>
                    <th className="text-left py-3 px-4 font-medium">Description</th>
                    <th className="text-left py-3 px-4 font-medium">Seasonal Pattern</th>
                    <th className="text-left py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {destinations.map((dest) => (
                    <tr key={dest.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <code className="text-sm bg-muted px-2 py-1 rounded">
                          {dest.id}
                        </code>
                      </td>
                      <td className="py-3 px-4 font-medium">{dest.name}</td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {dest.description || 'No description'}
                      </td>
                      <td className="py-3 px-4">
                        {dest.seasonal_pattern ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                            Configured
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                            Not Set
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => deleteDestination(dest.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Destination Types Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Available Destination Types</CardTitle>
          <CardDescription>
            Reference list of supported destination categories
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { id: 'tropical_beach_bali', name: 'Tropical Beach (Bali-specific)' },
              { id: 'tropical_beach', name: 'Tropical Beach (Generic)' },
              { id: 'tropical_urban', name: 'Tropical Urban' },
              { id: 'subtropical_beach', name: 'Subtropical Beach' },
              { id: 'subtropical_urban', name: 'Subtropical Urban' },
              { id: 'urban_cultural', name: 'Urban Cultural' },
              { id: 'mountain_resort', name: 'Mountain Resort' },
              { id: 'business_hub', name: 'Business Hub' },
              { id: 'luxury_hub', name: 'Luxury Hub' },
            ].map((type) => (
              <div key={type.id} className="p-3 border rounded-md">
                <code className="text-xs text-muted-foreground">{type.id}</code>
                <p className="font-medium">{type.name}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDestinations;
